import http from '@/utils/httpClient';
import { toaster } from '@/utils/toast';
import { Action, Thunk, action, thunk } from 'easy-peasy';

export type AdminUserType = 'provider' | 'advisor';

export interface AdminUser {
  userId: string;
  userTypeId: string | null;
  firstName: string;
  lastName: string;
  emailId: string;
  type: AdminUserType;
  phone: string | null;
  location: string | null;
  skills: string[];
  status: string;
  dateAdded: string;
  tosAcceptedAt: string | null;
  tosAcceptedVersion: string | null;
}

export interface AdminUserListResponse {
  users: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
}

export type AdminUserSortField = 'lastName' | 'firstName' | 'location' | 'type' | 'emailId';
export type AdminUserSortDir = 'asc' | 'desc';
export type AdminUserTypeFilter = 'all' | AdminUserType;

export interface AdminUserListQuery {
  q?: string;
  location?: string;
  skills?: string[]; // serialised as a comma-separated string on the wire
  type?: AdminUserTypeFilter;
  sortBy?: AdminUserSortField;
  sortDir?: AdminUserSortDir;
  page?: number;
  pageSize?: number;
}

export interface IAdminUserListModel {
  list: AdminUserListResponse | undefined;
  loading: boolean;
  skillsCatalog: string[] | undefined; // distinct provider skills, for the filter dropdown
  skillsCatalogLoading: boolean;

  setList: Action<IAdminUserListModel, AdminUserListResponse | undefined>;
  setLoading: Action<IAdminUserListModel, boolean>;
  setSkillsCatalog: Action<IAdminUserListModel, string[] | undefined>;
  setSkillsCatalogLoading: Action<IAdminUserListModel, boolean>;

  getUsers: Thunk<IAdminUserListModel, AdminUserListQuery | undefined>;
  getSkillsCatalog: Thunk<IAdminUserListModel>;
}

const AdminUserListModel: IAdminUserListModel = {
  list: undefined,
  loading: false,
  skillsCatalog: undefined,
  skillsCatalogLoading: false,

  setList: action((state, value) => {
    state.list = value;
  }),
  setLoading: action((state, value) => {
    state.loading = value;
  }),
  setSkillsCatalog: action((state, value) => {
    state.skillsCatalog = value;
  }),
  setSkillsCatalogLoading: action((state, value) => {
    state.skillsCatalogLoading = value;
  }),

  getUsers: thunk(async (action, payload) => {
    action.setLoading(true);
    try {
      // Map our query object to axios `params`. The backend treats the search
      // string as `search` (not `q`); skills are serialised as a CSV.
      const params: Record<string, string> = {};
      if (payload?.q) params.search = payload.q;
      if (payload?.location) params.location = payload.location;
      if (payload?.skills && payload.skills.length > 0) {
        params.skills = payload.skills.join(',');
      }
      if (payload?.type && payload.type !== 'all') params.type = payload.type;
      if (payload?.sortBy) params.sortBy = payload.sortBy;
      if (payload?.sortDir) params.sortDir = payload.sortDir;
      if (payload?.page) params.page = String(payload.page);
      if (payload?.pageSize) params.pageSize = String(payload.pageSize);

      const res = await http().get('/admin/users', { params });
      action.setList(res.data);
    } catch {
      toaster('error', 'Error in fetching users');
    } finally {
      action.setLoading(false);
    }
  }),

  getSkillsCatalog: thunk(async (action) => {
    action.setSkillsCatalogLoading(true);
    try {
      const res = await http().get('/admin/users/skills');
      action.setSkillsCatalog(res.data.skills);
    } catch {
      toaster('error', 'Error in fetching skill list');
    } finally {
      action.setSkillsCatalogLoading(false);
    }
  }),
};

export default AdminUserListModel;
